import React from "react";
import { graphql, PreloadedQuery, usePreloadedQuery } from "react-relay";
import { Grid, List, ListItem, ListItemText } from "@mui/material";
import { format, formatDuration, intervalToDuration, secondsToMilliseconds } from "date-fns";
import { Link } from "react-router-dom";
import { OpenquakeHazardSolutionDetailTabQuery } from "./__generated__/OpenquakeHazardSolutionDetailTabQuery.graphql";
import KeyValueTable from "../../components/common/KeyValueTable";
import FileTable from "../../components/common/FileTable";
import { viewPath } from "../../utils/routeUtils";

interface OpenquakeHazardSolutionDetailTabProps {
  queryRef: PreloadedQuery<OpenquakeHazardSolutionDetailTabQuery, Record<string, unknown>>;
}

const OpenquakeHazardSolutionDetailTab: React.FC<OpenquakeHazardSolutionDetailTabProps> = ({
  queryRef,
}: OpenquakeHazardSolutionDetailTabProps) => {
  const data = usePreloadedQuery<OpenquakeHazardSolutionDetailTabQuery>(
    openquakeHazardSolutionDetailTabQuery,
    queryRef,
  );

  const created = data?.node?.created ? (data?.node?.created as string) : undefined;
  const formattedDate = created ? format(new Date(created), "PPPppp") : "";

  const duration = data?.node?.produced_by?.duration
    ? formatDuration(
        intervalToDuration({
          start: 0,
          end: secondsToMilliseconds(data?.node?.produced_by?.duration),
        }),
      )
    : "-";

  return (
    <>
      <Grid container spacing={0} wrap={"nowrap"}>
        <Grid columns={6}>
          <List dense>
            <ListItem>
              <ListItemText primary="Created" secondary={formattedDate} />
            </ListItem>
          </List>
          <ListItem>
            <ListItemText
              primary="Produced By"
              secondary={<Link to={viewPath(data?.node?.produced_by?.id)}>{data?.node?.produced_by?.id}</Link>}
            />
          </ListItem>
        </Grid>
        <Grid>
          <List>
            <ListItem>
              <ListItemText primary="Model Type" secondary={data?.node?.produced_by?.model_type} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Duration" secondary={duration} />
            </ListItem>
          </List>
        </Grid>
      </Grid>
      <FileTable title="Hazard Solution" files={[data?.node?.csv_archive, data?.node?.hdf5_archive]} />
      {data.node?.config && (
        <KeyValueTable
          header="Config"
          strong={true}
          data={[
            { k: "ID", v: data.node.config.id },
            { k: "Created", v: data.node.config.created },
          ]}
          style={{ marginTop: "10px", marginBottom: "10px" }}
        />
      )}
      {data?.node?.task_args && <FileTable title="Template Archive" files={[data?.node?.task_args]} />}
      {data.node?.config?.source_models && (
        <span>
          <strong>WARNING: </strong> Source Models are not expected to be correct after 17/12/2023
          <FileTable title="Source Models" files={data.node?.config?.source_models} />
        </span>
      )}
    </>
  );
};

export const openquakeHazardSolutionDetailTabQuery = graphql`
  query OpenquakeHazardSolutionDetailTabQuery($id: ID!) {
    node(id: $id) {
      ... on OpenquakeHazardSolution {
        id
        created
        produced_by {
          id
          model_type
          duration
        }
        task_args {
          file_name
          file_url
          id
        }
        config {
          id
          created
          source_models {
            ... on Node {
              id
            }
            ... on FileInterface {
              file_name
              file_url
            }
          }
        }
        csv_archive {
          id
          file_name
          file_url
        }
        hdf5_archive {
          id
          file_name
          file_url
        }
      }
    }
  }
`;

export default OpenquakeHazardSolutionDetailTab;
